import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import { useDocumentLanguage } from '@/i18n/useDocumentLanguage';
import { useAuthContext } from '@/contexts/AuthContext';
import ProblemReporter from '@/components/ProblemReporter';
import OnboardingMessagesLink from '@/components/OnboardingMessagesLink';
import AgeConfirmationGate from '@/components/AgeConfirmationGate';
import { FloatingControls } from '@/components/ui/PageControls';

// Round 57, B — owner ruling: learner routes only. Admins have other
// channels; pre-auth pages (welcome/sign-in/sign-up) have no identity yet
// for a report to carry. Phase G — moved from being App.tsx's own sibling
// of <Routes> into here: mounted OUTSIDE this file's dir/lang wrapper, it
// never picked up RTL at all (found live, G11) — a floating `fixed`
// element's `inset-inline-*` position is resolved from ITS OWN inherited
// `direction`, which only reaches it by being a real DOM descendant of the
// dir="rtl" wrapper below, not by being a sibling of the router output.
// The `pathname.startsWith('/admin')` guard is now redundant in principle
// (LearnerLayout is never rendered for /admin at all — see App.tsx's route
// tree) but is left in as a second, independent guard rather than removed.
function ProblemReporterGate() {
  const { user } = useAuthContext();
  const { pathname } = useLocation();
  if (!user || pathname.startsWith('/admin')) return null;
  return <ProblemReporter />;
}

/**
 * Phase G, G4/G5 — the ONLY place `dir`/`lang` attributes are ever set in
 * this app. Mounted in App.tsx as the `element` on a layout Route whose
 * children are every learner-facing route (pre-auth included: welcome,
 * sign-in, sign-up, forgot-password, onboarding/*, lessons/*, profile,
 * subscribe, payment-success, plus the legacy redirects and the catch-
 * all). The `/admin` route tree is a SIBLING of that layout route in
 * App.tsx, never a descendant — confirmed by reading App.tsx's actual
 * route tree, not assumed. That makes it structurally impossible for any
 * admin route to render inside this component, so `dir="rtl"` can never
 * reach `/admin` no matter what a learner elsewhere in the same browser
 * tab has selected. The other half of "admin is always English" — the
 * TEXT itself, not just the direction — is enforced independently in
 * i18n/config.ts (the `admin` namespace is never loaded for any
 * non-English language, for any component, regardless of where it's
 * rendered).
 */
function LearnerShell({ children }: { children: React.ReactNode }) {
  const { language, dir } = useLanguage();
  // Round 154b D4 - the wrapper below sets lang/dir for the learner's page, but the <html> element used to keep lang="en" dir=ltr in the Arabic UI
  // (screen readers pick the voice from <html lang>, the browser's find and form controls from <html dir>). The learner tree sets them on <html> too and
  // gives them back when it unmounts, so an admin route (a sibling of this tree, always English) never keeps a learner's rtl.
  useDocumentLanguage(language, dir);
  return (
    // NO `key` on this wrapper, deliberately. It used to be
    // `key={`${language}-${generation}`}`, a full remount of the learner
    // subtree on every language change (the ~800 `t()` call sites are plain
    // function calls that subscribe to nothing, so a remount was the way
    // to repaint them). That remount destroyed all local component state:
    // a learner who changed language half-way through the sign-up or
    // profile-setup form lost every field already typed. Text is now
    // refreshed by App.tsx re-rendering the route tree on i18next's
    // `languageChanged` event (useRerenderOnLanguageChange) — a re-render,
    // not a remount, so state survives. Do not put the language (or
    // `generation`) back into a key anywhere on the learner tree;
    // frontend/src/i18n/languageChangeKeepsFormState.test.tsx fails if
    // anyone does. `display: contents` (the `contents` class) keeps this
    // wrapper out of layout entirely.
    <div dir={dir} lang={language} className="contents">
      {/* Round 145: the language + theme switches on every learner screen that shows no TopBar / PageControls of its own - a row at the TOP,
          so it renders before the page (rendered after it, it ended up at the bottom of the page; found by the browser run) */}
      <FloatingControls level="learner" />
      {children}
      <ProblemReporterGate />
      <OnboardingMessagesLink />
      <AgeConfirmationGate />
    </div>
  );
}

export default function LearnerLayout() {
  return (
    <LanguageProvider>
      <LearnerShell>
        <Outlet />
      </LearnerShell>
    </LanguageProvider>
  );
}
